import React, { useEffect, useRef, useState } from 'react';
import { CalendarDays, Brain, Clock } from 'lucide-react';
import { weeklySpecials, triviaNight } from '../config/siteConfig';

/**
 * Weekly Specials board — one special per day, styled like the bar's
 * gold-on-dark specials flyer. Today's row is highlighted automatically,
 * and the standing trivia night gets its own feature card below.
 */
const WeeklySpecialsSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const todayName = new Date().toLocaleDateString('en-US', { weekday: 'long' });

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="specials" ref={sectionRef} className="py-20 lg:py-28 bg-[#F7EDD9] texture-paper relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#D4A574]/40 to-transparent" />
      <div className="hidden md:block absolute top-20 left-0 w-80 h-80 bg-[#C9A227]/5 rounded-full blur-3xl" />

      <div className="relative z-10 max-w-6xl mx-auto px-5">
        {/* Header */}
        <div className={`text-center mb-12 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="text-[#C9A227] font-semibold tracking-widest text-sm uppercase flex items-center justify-center gap-2">
            <CalendarDays className="w-4 h-4" />
            Every Day's Got One
          </span>
          <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl text-[#2C2416] tracking-wide mt-3 leading-[0.95]">
            {weeklySpecials.title}
          </h2>
          <p className="mt-4 text-lg text-[#2C2416]/75 max-w-xl mx-auto">
            {weeklySpecials.subtitle}
          </p>
        </div>

        {/* The board */}
        <div className={`max-w-3xl mx-auto chalkboard rounded-lg p-6 sm:p-8 shadow-warm-xl transition-all duration-500 delay-150 ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
          <ul className="divide-y divide-[#C9A227]/15">
            {weeklySpecials.days.map((item) => {
              const isToday = item.day === todayName;
              return (
                <li
                  key={item.day}
                  className={`flex flex-col gap-1 sm:flex-row sm:flex-wrap sm:items-baseline sm:gap-x-4 sm:gap-y-1 py-3.5 px-3 sm:px-4 rounded-md transition-colors ${
                    isToday ? 'bg-[#C9A227]/15 ring-1 ring-[#C9A227]/40' : ''
                  }`}
                >
                  <span className={`font-display text-xl sm:text-2xl tracking-wide sm:w-40 shrink-0 ${isToday ? 'text-[#C9A227]' : 'text-[#D4A574]'}`}>
                    {item.day.toUpperCase()}
                    {isToday && (
                      <span className="ml-2 align-middle text-[10px] font-sans font-bold tracking-widest bg-[#C9A227] text-[#1a1612] px-1.5 py-0.5 rounded">
                        TODAY
                      </span>
                    )}
                  </span>
                  <span className={`text-base sm:text-lg ${isToday ? 'text-[#FDF6E9]' : 'text-[#FDF6E9]/80'}`}>
                    {item.special}
                  </span>
                  {item.badge && (
                    <span className="self-start text-xs font-bold tracking-wide uppercase bg-[#C9A227]/20 text-[#D4A574] border border-[#C9A227]/30 px-2 py-0.5 rounded-full">
                      {item.badge}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>

        {/* Trivia Thursdays feature card */}
        {triviaNight.enabled && (
          <div className={`max-w-3xl mx-auto mt-8 transition-all duration-700 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="relative bg-gradient-to-br from-[#1a1612] to-[#2C2416] border-2 border-[#C9A227]/40 rounded-lg p-6 sm:p-8 overflow-hidden">
              <span aria-hidden="true" className="absolute -top-2 left-6 text-5xl text-[#C9A227]/15 font-serif select-none">?</span>
              <span aria-hidden="true" className="absolute bottom-2 right-8 text-6xl text-[#C9A227]/10 font-serif select-none">?</span>

              <div className="relative flex flex-col sm:flex-row sm:items-center gap-5">
                <div className="w-14 h-14 shrink-0 bg-[#C9A227]/15 border border-[#C9A227]/40 rounded-full flex items-center justify-center">
                  <Brain className="w-7 h-7 text-[#C9A227]" />
                </div>
                <div className="flex-1">
                  <h3 className="font-display text-3xl sm:text-4xl text-[#FDF6E9] tracking-wide">
                    {triviaNight.headline}
                  </h3>
                  <p className="text-[#D4A574] font-accent italic text-lg mt-1">{triviaNight.tagline}</p>
                  <p className="text-[#FDF6E9]/60 text-sm mt-2">
                    {triviaNight.details.split('•').map((seg, i, arr) => (
                      <React.Fragment key={i}>
                        <span className="whitespace-nowrap">{seg.trim()}</span>
                        {i < arr.length - 1 && <span aria-hidden="true"> • </span>}
                      </React.Fragment>
                    ))}
                  </p>
                </div>
                <div className="shrink-0 text-left sm:text-right">
                  <div className="flex items-center sm:justify-end gap-2 text-[#C9A227]">
                    <Clock className="w-4 h-4" />
                    <span className="font-display text-xl tracking-wide">EVERY {triviaNight.day.toUpperCase()}</span>
                  </div>
                  <p className="text-[#FDF6E9]/80 font-semibold mt-1">{triviaNight.time}</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default WeeklySpecialsSection;
